import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Modal, Form,
  Input, Select, InputNumber, message, Popconfirm, Badge
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { getConfLocations, createConfLocation, updateConfLocation, deleteConfLocation, getEvents } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

const LOC_TYPES = ['Hall', 'Zone', 'Lobby', 'Registration', 'Exhibition', 'Workshop', 'Cafeteria', 'Parking', 'Other'];
const TYPE_COLOR = { Hall: 'blue', Zone: 'purple', Registration: 'green', Exhibition: 'orange', Workshop: 'cyan', Cafeteria: 'gold', Parking: 'default', Lobby: 'geekblue', Other: 'default' };

export default function ConferenceLocationMaster() {
  const [locations, setLocations] = useState([]);
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [filterEvent, setFilterEvent] = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    const params = filterEvent ? { event_id: filterEvent } : {};
    getConfLocations(params).then(r => setLocations(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    getEvents().then(r => {
      setEvents(r.data.data || []);
      const active = (r.data.data || []).find(e => e.status === 'active');
      if (active) setFilterEvent(active.id);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filterEvent]); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    if (filterEvent) form.setFieldValue('event_id', filterEvent);
    setModalOpen(true);
  };
  const openEdit = (rec) => { setEditing(rec); form.setFieldsValue(rec); setModalOpen(true); };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      if (editing) { await updateConfLocation(editing.id, vals); message.success('Location updated'); }
      else         { await createConfLocation(vals);             message.success('Location added'); }
      setModalOpen(false); load();
    } catch (e) { if (!e.errorFields) message.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    try { await deleteConfLocation(id); message.success('Location deleted'); load(); }
    catch (e) { message.error('Delete failed'); }
  };

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Code', dataIndex: 'location_code', render: v => <Tag>{v}</Tag>, width: 100 },
    { title: 'Location Name', dataIndex: 'location_name', render: v => <Text strong>{v}</Text>, sorter: (a, b) => a.location_name.localeCompare(b.location_name) },
    { title: 'Type', dataIndex: 'location_type', render: v => <Tag color={TYPE_COLOR[v] || 'default'}>{v}</Tag>, width: 110 },
    { title: 'Event', dataIndex: 'event_name', render: v => v || '-' },
    { title: 'Capacity', dataIndex: 'capacity', render: v => v > 0 ? v : '-', width: 90, align: 'center' },
    { title: 'Current Count', dataIndex: 'current_count', render: v => <Badge count={v || 0} style={{ background: v > 0 ? '#1B2B8C' : '#d9d9d9' }} showZero />, width: 120, align: 'center' },
    { title: 'Readers', dataIndex: 'reader_count', render: v => v || 0, width: 80, align: 'center' },
    { title: 'Floor', dataIndex: 'floor', render: v => v || '-', width: 80 },
    { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>, width: 80 },
    {
      title: 'Actions', width: 100, render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete location?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <EnvironmentOutlined style={{ marginRight: 8 }} />Location Master
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Manage halls, zones and areas for conference events</Text>
        </Col>
        <Col>
          <Space>
            <Select value={filterEvent} onChange={setFilterEvent} style={{ width: 200 }} placeholder="Filter by Event" allowClear>
              {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: '#0F1D6B' }}>
              Add Location
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Summary */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {LOC_TYPES.slice(0, 6).map(t => {
          const cnt = locations.filter(l => l.location_type === t).length;
          return cnt > 0 ? (
            <Col key={t}>
              <Tag color={TYPE_COLOR[t]} style={{ fontSize: 12, padding: '4px 10px' }}>{t}: {cnt}</Tag>
            </Col>
          ) : null;
        })}
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <Table columns={columns} dataSource={locations} rowKey="id" loading={loading}
          size="small" pagination={{ pageSize: 15, showSizeChanger: true }}
          scroll={{ x: 900 }} />
      </Card>

      <Modal title={editing ? 'Edit Location' : 'Add Location'} open={modalOpen}
        onOk={handleSave} onCancel={() => setModalOpen(false)} width={600}
        okButtonProps={{ style: { background: '#0F1D6B' } }}>
        <Form form={form} layout="vertical" size="middle">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="location_name" label="Location Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Main Hall A" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="location_code" label="Code" rules={[{ required: true }]}>
                <Input placeholder="HALL-A" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="location_type" label="Type" rules={[{ required: true }]}>
                <Select>
                  {LOC_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="capacity" label="Capacity">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="event_id" label="Event">
                <Select placeholder="Select event" allowClear>
                  {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="floor" label="Floor">
                <Input placeholder="Ground, 1st..." />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="building" label="Building">
                <Input placeholder="Block A..." />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
